import { useEffect, useRef, useState } from 'react'
import { Send } from 'lucide-react'
import { api } from '@/lib/api'

interface Snippet {
  id: string
  name: string
  shortcut: string | null
  body: string
  subject: string | null
}

interface ComposerProps {
  channel: string
  onSend: (body: string, snippetId?: string) => Promise<void>
}

export function Composer({ channel, onSend }: ComposerProps) {
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [snippets, setSnippets] = useState<Snippet[]>([])
  const [picker, setPicker] = useState<{ q: string; activeIdx: number } | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    void api<{ items: Snippet[] }>('/v1/marketing/snippets')
      .then((r) => setSnippets(r.items))
      .catch(() => undefined)
  }, [])

  const filtered = picker
    ? snippets
        .filter((s) => {
          const q = picker.q.toLowerCase()
          if (!q) return true
          return (
            s.shortcut?.toLowerCase().includes(q) || s.name.toLowerCase().includes(q)
          )
        })
        .slice(0, 6)
    : []

  function handleChange(value: string) {
    setBody(value)
    // Detectar trigger "/" al inicio o tras espacio
    const cursor = textareaRef.current?.selectionStart ?? value.length
    const upToCursor = value.slice(0, cursor)
    const match = upToCursor.match(/(?:^|\s)\/([a-z0-9-]*)$/i)
    if (match) {
      setPicker({ q: match[1] ?? '', activeIdx: 0 })
    } else {
      setPicker(null)
    }
  }

  function insertSnippet(s: Snippet) {
    const cursor = textareaRef.current?.selectionStart ?? body.length
    const upToCursor = body.slice(0, cursor)
    const fromCursor = body.slice(cursor)
    // Reemplazar el "/shortcut" parcial por el body del snippet
    const cleaned = upToCursor.replace(/(?:^|\s)\/([a-z0-9-]*)$/i, (m) =>
      m.startsWith(' ') ? ' ' : '',
    )
    const next = cleaned + s.body + fromCursor
    setBody(next)
    setPicker(null)
    setTimeout(() => {
      textareaRef.current?.focus()
      const pos = cleaned.length + s.body.length
      textareaRef.current?.setSelectionRange(pos, pos)
    }, 0)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!body.trim() || busy) return
    setBusy(true)
    try {
      await onSend(body)
      setBody('')
    } catch {
      // error mostrado por el padre
    } finally {
      setBusy(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (picker) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setPicker((p) =>
          p ? { ...p, activeIdx: Math.min(p.activeIdx + 1, filtered.length - 1) } : null,
        )
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setPicker((p) => (p ? { ...p, activeIdx: Math.max(0, p.activeIdx - 1) } : null))
        return
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        const target = filtered[picker.activeIdx]
        if (target) {
          e.preventDefault()
          insertSnippet(target)
          return
        }
      }
      if (e.key === 'Escape') {
        setPicker(null)
        return
      }
    }
    // Ctrl/Cmd + Enter envía
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void submit(e as unknown as React.FormEvent)
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="px-4 py-3 relative">
      {picker && filtered.length > 0 && (
        <div className="absolute bottom-full left-4 right-4 mb-1 bp-card overflow-hidden">
          <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider text-ink-700/60 border-b border-ink-900/10">
            snippets · usa ↑↓ + enter
          </div>
          <ul>
            {filtered.map((s, idx) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => insertSnippet(s)}
                  className={[
                    'w-full text-left px-3 py-2 flex items-center gap-2',
                    idx === picker.activeIdx ? 'bg-blueprint-50' : 'hover:bg-surface-alt',
                  ].join(' ')}
                >
                  {s.shortcut && (
                    <span className="font-mono text-xs text-blueprint-700 shrink-0">
                      /{s.shortcut}
                    </span>
                  )}
                  <span className="text-sm flex-1 truncate">{s.name}</span>
                  <span className="text-xs text-ink-700/50 truncate max-w-[200px]">
                    {s.body.slice(0, 60)}…
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex items-end gap-2">
        <textarea
          data-testid="composer-input"
          ref={textareaRef}
          value={body}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Escribir mensaje · canal ${channel} · "/" para snippets · ⌘+Enter envía`}
          rows={2}
          className="flex-1 border border-ink-900/15 rounded px-3 py-2 text-sm font-sans resize-none min-h-[44px] max-h-32"
        />
        <button
          data-testid="composer-send"
          type="submit"
          disabled={!body.trim() || busy}
          className="bp-btn-primary"
          title="Enviar (⌘+Enter)"
        >
          <Send className="size-3.5" />
          {busy ? '…' : 'Enviar'}
        </button>
      </div>
    </form>
  )
}
